import { cn } from "../../lib/utils";

type PortfolioHeaderProps = {
  rank: number;
  participantCount: number;
  pointsBalance: number;
  deltaWithLeader: number;
  leaderPointsBalance: number;
  totalPnL: number;
  unrealizedPnL: number;
  realizedPnL: number;
  numberFormatter: Intl.NumberFormat;
};

export function PortfolioHeader({
  rank,
  participantCount,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pointsBalance,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deltaWithLeader,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  leaderPointsBalance,
  totalPnL,
  unrealizedPnL,
  realizedPnL,
  numberFormatter,
}: PortfolioHeaderProps) {
  const formatSigned = (value: number) => `${value >= 0 ? "+" : "-"}${numberFormatter.format(Math.abs(value))}`;
  const oneDecimalFormatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  const formatSignedOneDecimal = (value: number) =>
    `${value >= 0 ? "+" : "-"}${oneDecimalFormatter.format(Math.abs(value))}`;
  const mobileBackgroundClass =
    totalPnL > 0
      ? "border-success/60 bg-[radial-gradient(circle_at_20%_10%,rgba(34,197,94,0.2),rgba(4,9,20,0.95)_58%)]"
      : totalPnL < 0
        ? "border-danger/60 bg-[radial-gradient(circle_at_20%_10%,rgba(239,68,68,0.2),rgba(4,9,20,0.95)_58%)]"
        : "border-white/20 bg-[radial-gradient(circle_at_20%_10%,rgba(148,163,184,0.18),rgba(4,9,20,0.95)_58%)]";

  const desktopBackgroundClass =
    totalPnL > 0
      ? "border-success/50 bg-success/5"
      : totalPnL < 0
        ? "border-danger/50 bg-danger/5"
        : "border-white/20 bg-background-card/90";

  return (
    <>
      <div className={cn("rounded-3xl border p-3 shadow-[0_10px_26px_rgba(0,0,0,0.35)] md:p-4", mobileBackgroundClass)}>
        <div className="flex items-start justify-between gap-3 md:grid md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-5 lg:grid-cols-[minmax(0,1fr)_auto]">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Total P&amp;L</p>
            <p
              className={cn(
                "mt-1 truncate text-[29px] font-bold leading-none md:text-[38px] lg:text-[42px]",
                totalPnL >= 0 ? "text-success" : "text-danger",
              )}
            >
              {formatSignedOneDecimal(totalPnL)}
            </p>
          </div>

          <div className="shrink-0 rounded-full border border-white/15 bg-background/80 px-4 py-3 text-center md:px-5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.1em] text-foreground-muted">Rank</p>
            <p className="text-[20px] font-bold leading-none text-foreground md:text-[24px]">{rank}/{participantCount}</p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 md:gap-3">
          <div className="rounded-xl border border-white/10 bg-background/65 px-3 py-2 md:px-4 md:py-3">
            <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Realized</p>
            <p className={cn("mt-1 text-[25px] font-bold leading-none md:text-[32px]", realizedPnL >= 0 ? "text-success" : "text-danger")}>
              {formatSigned(realizedPnL)}
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-background/65 px-3 py-2 md:px-4 md:py-3">
            <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Trading</p>
            <p className={cn("mt-1 text-[25px] font-bold leading-none md:text-[32px]", unrealizedPnL >= 0 ? "text-success" : "text-danger")}>
              {formatSigned(unrealizedPnL)}
            </p>
          </div>
        </div>
      </div>

      <div className={cn("hidden min-h-14 items-center rounded-xl border px-6 shadow-[0_8px_18px_rgba(0,0,0,0.24)]", desktopBackgroundClass)}>
        <Metric label="Rank" value={`${rank}/${participantCount}`} />
        <Divider />
        <Metric label="Realized" value={formatSigned(realizedPnL)} tone={realizedPnL >= 0 ? "positive" : "negative"} />
        <Divider />
        <Metric label="Trading" value={formatSigned(unrealizedPnL)} tone={unrealizedPnL >= 0 ? "positive" : "negative"} />
        <Divider />
        <Metric label="Total Value" value={formatSigned(totalPnL)} tone={totalPnL >= 0 ? "positive" : "negative"} />
      </div>
    </>
  );
}

function Metric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: "default" | "positive" | "negative";
}) {
  return (
    <div className="flex min-w-0 flex-1 items-baseline justify-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground-muted">{label}</span>
      <span
        className={cn(
          "text-[28px] font-semibold leading-none text-foreground",
          tone === "positive" && "text-success",
          tone === "negative" && "text-danger",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return (
    <div className="mx-4 h-7 w-px shrink-0 bg-white/10" aria-hidden="true" />
  );
}
